import { useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  useRoleContext,
  useShipmentPDFExport,
  useShipmentQuotes,
  useShipmentRoute,
} from "hooks";
import { getPublicShipmentTrackingUrlOrNull } from "utils";

import { getShipmentExportPDFRenderConfig } from "./utils";
import { ClientAppEvent } from "../../../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  shipment: ShipperCondensedShipment | CarrierCondensedShipment;
}

export function ExportShipmentToPDFModal(props: Props) {
  const { open, onClose, shipment } = props;
  const posthog = usePostHog();
  const [includePrice, setIncludePrice] = useState<boolean>(false);
  const [includeCarrierInfo, setIncludeCarrierInfo] = useState<boolean>(false);
  const shipmentRef = useRef<undefined | CondensedShipment>();
  const shipmentStatusRef = useRef<undefined | ShipmentStatusValue>();
  const { role } = useRoleContext();
  const {
    route,
    error: routeFetchError,
    loading: routeLoading,
    refetchRoute,
  } = useShipmentRoute(shipment?.info.route);
  const {
    quotes,
    refetchQuotes,
    isLoading: quoteRefetchLoading,
  } = useShipmentQuotes(shipment?.info.id);

  useEffect(() => {
    /*
    This hook keeps track of the shipment ID between renders to ensure
    that the route is re-fetched when another shipment is selected.
    */
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      shipmentStatusRef.current = shipment.shipment_status;
      refetchRoute();
      refetchQuotes();
    }
  }, [shipment, refetchRoute, refetchQuotes]);

  useEffect(() => {
    // hook to re-fetch quotes if the shipment booked
    if (shipment && shipment.shipment_status !== shipmentStatusRef.current) {
      shipmentStatusRef.current = shipment.shipment_status;
      refetchQuotes();
    }
  }, [shipment, refetchQuotes]);

  const publicShipmentUrl = getPublicShipmentTrackingUrlOrNull({
    publicToken: shipment.public_token,
    shipmentType: shipment.info.type,
  });
  const { exportShipmentToPDF, isExporting, error } = useShipmentPDFExport({
    shipment,
    publicShipmentUrl,
  });

  const handleClose = () => {
    setIncludePrice(false);
    setIncludeCarrierInfo(false);
    onClose();
  };

  const priceAndCarrierInfoAvailable =
    shipment.shipment_status !== "ACTIVE" &&
    shipment.shipment_status !== "EXPIRED" &&
    shipment.shipment_status !== "CANCELLED";
  const OptionsTextColor = priceAndCarrierInfoAvailable
    ? "text.primary"
    : "text.secondary";

  const handleExport = () => {
    const pdfRenderConfig = getShipmentExportPDFRenderConfig(
      shipment,
      includePrice,
      route,
      quotes,
      includeCarrierInfo,
    );
    posthog?.capture(ClientAppEvent.SHIPMENT_EXPORTED_TO_PDF, {
      export_params: {
        include_price: includePrice,
        include_carrier_info: includeCarrierInfo,
        price_and_carrier_info_available: priceAndCarrierInfoAvailable,
      },
      shipment_id: shipment.info.id,
      triggered_by_role: role?.id,
      role_type: role?.type,
    });
    exportShipmentToPDF(pdfRenderConfig).then(() => {
      handleClose();
    });
  };

  return (
    <Box>
      <Dialog fullWidth open={open} onClose={onClose}>
        <DialogTitle>Export to PDF</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={includePrice}
                onChange={(e) => setIncludePrice(e.target.checked)}
                disabled={!priceAndCarrierInfoAvailable}
                size="medium"
              />
            }
            label={
              <Typography variant="h6" color={OptionsTextColor}>
                Include pricing details in export (booked price and currency)
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeCarrierInfo}
                onChange={(e) => setIncludeCarrierInfo(e.target.checked)}
                size="medium"
                disabled={!priceAndCarrierInfoAvailable}
              />
            }
            label={
              <Typography variant="h6" color={OptionsTextColor}>
                Include carrier information in export
              </Typography>
            }
          />
          {!priceAndCarrierInfoAvailable && (
            <Alert variant="outlined" severity="info" sx={{ mt: "1rem" }}>
              Pricing and carrier details are only available for booked
              shipments.
            </Alert>
          )}
          <ErrorTypography error={error} />
          <ErrorTypography error={routeFetchError} />
        </DialogContent>
        <LoadingWrappedNode
          loading={isExporting || routeLoading || quoteRefetchLoading}
        >
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleExport}
              autoFocus
              color="primary"
              variant="contained"
            >
              Download PDF
            </Button>
          </DialogActions>
        </LoadingWrappedNode>
      </Dialog>
    </Box>
  );
}
